import {
    TechnicalVisitFormDefinition,
    TechnicalVisitPayloadResponse,
    TechnicalVisitParsedResponse,
    TechnicalVisitQuestion,
    TechnicalVisitSection
} from '../types/technicalVisit.types';

export const getValue = (value: unknown): string | number => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (typeof value === 'string' || typeof value === 'number') return value;

    return String(value);
};

export const getBooleanLabel = (value: unknown): string => {
    if (value === true || value === 'true') return 'Si';
    if (value === false || value === 'false') return 'No';

    return 'N/A';
};

export const getDefaultAnswerId = (question: TechnicalVisitQuestion) =>
    question.tiposRespuesta.find((answer) => answer.valorDefecto)?.id ||
    question.tiposRespuesta[0]?.id ||
    0;

export const getPayloadAnswerTypeId = (question: TechnicalVisitQuestion, answerId: number) => {
    const selectedAnswer = question.tiposRespuesta.find((answer) => answer.id === answerId);
    if (!selectedAnswer) return 3;

    const normalizedDescription = selectedAnswer.descripcion.trim().toUpperCase();

    if (normalizedDescription === 'SI') return 1;
    if (normalizedDescription === 'NO') return 2;
    if (normalizedDescription === 'N/A' || normalizedDescription === 'NA') return 3;

    return selectedAnswer.orden;
};

export const getAnswerIdFromPayloadType = (
    question: TechnicalVisitQuestion,
    payloadAnswerTypeId?: number
) => {
    if (!payloadAnswerTypeId) return undefined;

    const answerByDescription = question.tiposRespuesta.find((answer) => {
        const normalizedDescription = answer.descripcion.trim().toUpperCase();

        return (
            (payloadAnswerTypeId === 1 && normalizedDescription === 'SI') ||
            (payloadAnswerTypeId === 2 && normalizedDescription === 'NO') ||
            (payloadAnswerTypeId === 3 &&
                (normalizedDescription === 'N/A' || normalizedDescription === 'NA'))
        );
    });

    return (
        answerByDescription?.id ??
        question.tiposRespuesta.find((answer) => answer.orden === payloadAnswerTypeId)?.id
    );
};

export const getEmployeeCount = (establecimiento: any) => {
    if (establecimiento?.personal) return establecimiento.personal;

    const expuestos = establecimiento?.nomina?.expuestos;
    const noExpuestos = establecimiento?.nomina?.noExpuestos;

    if (typeof expuestos === 'number' || typeof noExpuestos === 'number') {
        return (expuestos ?? 0) + (noExpuestos ?? 0);
    }

    return 'N/A';
};

const normalizeTechnicalVisitAnswer = (answer: any, index: number) => ({
    id: Number(answer?.id ?? answer?.idTipoRespuesta ?? index + 1),
    descripcion: String(answer?.descripcion ?? answer?.nombre ?? answer?.texto ?? ''),
    requiereRecomendacion: Boolean(
        answer?.requiereRecomendacion ??
        answer?.requiereObservacion ??
        (String(answer?.descripcion ?? answer?.nombre ?? answer?.texto ?? '').trim().toUpperCase() === 'NO')
    ),
    tipoComponente: 'radio_button' as const,
    valorDefecto: Boolean(answer?.valorDefecto ?? answer?.default ?? false),
    orden: Number(answer?.orden ?? index + 1),
    selected: Boolean(answer?.selected)
});

const buildDefaultTechnicalVisitAnswers = () => [
    {
        id: 1,
        descripcion: 'SI',
        requiereRecomendacion: false,
        tipoComponente: 'radio_button' as const,
        valorDefecto: false,
        orden: 1
    },
    {
        id: 2,
        descripcion: 'NO',
        requiereRecomendacion: true,
        tipoComponente: 'radio_button' as const,
        valorDefecto: false,
        orden: 2
    },
    {
        id: 3,
        descripcion: 'N/A',
        requiereRecomendacion: false,
        tipoComponente: 'radio_button' as const,
        valorDefecto: true,
        orden: 3
    }
];

const dedupeTechnicalVisitQuestions = (questions: TechnicalVisitQuestion[]) => {
    const uniqueQuestions = new Map<string, TechnicalVisitQuestion>();

    questions.forEach((question) => {
        const normalizedText = question.texto.trim().toUpperCase();
        const key = `${question.orden}-${normalizedText}`;

        if (!uniqueQuestions.has(key)) {
            uniqueQuestions.set(key, question);
        }
    });

    return Array.from(uniqueQuestions.values()).sort((a, b) => a.orden - b.orden);
};

const normalizeTechnicalVisitRecomendacion = (recomendacion: any, index: number) => ({
    id: Number(recomendacion?.id ?? index + 1),
    texto: String(recomendacion?.texto ?? recomendacion?.descripcion ?? '')
});

const normalizeTechnicalVisitQuestion = (question: any, index: number) => ({
    id: Number(question?.id ?? question?.idPregunta ?? index + 1),
    texto: String(question?.texto ?? question?.descripcion ?? question?.pregunta ?? ''),
    codigo: Number(question?.codigo ?? question?.codigoPregunta ?? index + 1),
    orden: Number(question?.orden ?? index + 1),
    tiposRespuesta: Array.isArray(question?.tiposRespuesta ?? question?.respuestas)
        ? (question?.tiposRespuesta ?? question?.respuestas).map(normalizeTechnicalVisitAnswer)
        : [],
    recomendaciones: Array.isArray(question?.recomendaciones)
        ? question.recomendaciones.map(normalizeTechnicalVisitRecomendacion)
        : []
});

const normalizeTechnicalVisitSection = (section: any, index: number): TechnicalVisitSection => ({
    id: Number(section?.id ?? section?.idSeccion ?? index + 1),
    titulo: String(section?.titulo ?? section?.nombre ?? section?.descripcion ?? ''),
    orden: Number(section?.orden ?? index + 1),
    preguntas: Array.isArray(section?.preguntas)
        ? section.preguntas
              .map(normalizeTechnicalVisitQuestion)
              .sort((a: any, b: any) => a.orden - b.orden)
        : []
});

export const normalizeTechnicalVisitFormDefinition = (
    response: any
): TechnicalVisitFormDefinition | null => {
    const payload = response?.data ?? response;
    const payloadSections = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : null;
    const sectionsSource = payload?.secciones ?? payload?.sections;
    const questionsSource = payload?.preguntas ?? payload?.questions;
    const responsesSource = payload?.respuestas ?? payload?.responses;

    const secciones = Array.isArray(payloadSections)
        ? payloadSections.map(normalizeTechnicalVisitSection)
        : Array.isArray(sectionsSource)
          ? sectionsSource.map(normalizeTechnicalVisitSection)
          : Array.isArray(questionsSource)
            ? [
                  {
                      id: Number(payload?.id ?? 1),
                      titulo: String(payload?.titulo ?? payload?.nombre ?? ''),
                      orden: 1,
                      preguntas: questionsSource
                          .map(normalizeTechnicalVisitQuestion)
                          .sort((a: any, b: any) => a.orden - b.orden)
                  }
              ]
            : Array.isArray(responsesSource)
              ? [
                    {
                        id: Number(payload?.id ?? 1),
                        titulo: String(payload?.titulo ?? payload?.nombre ?? ''),
                        orden: 1,
                        preguntas: dedupeTechnicalVisitQuestions(
                            responsesSource.map((item: any, index: number) => ({
                                id: Number(item?.idSeccion ?? item?.seccion?.id ?? index + 1),
                                texto: String(
                                    item?.seccion?.titulo ??
                                        item?.seccion?.descripcion ??
                                        item?.titulo ??
                                        ''
                                ),
                                codigo: Number(
                                    item?.seccion?.codigoDenuncia ??
                                        item?.codigoDenuncia ??
                                        index + 1
                                ),
                                orden: Number(item?.seccion?.orden ?? item?.orden ?? index + 1),
                                tiposRespuesta: buildDefaultTechnicalVisitAnswers(),
                                recomendaciones: []
                            }))
                        )
                    }
                ]
              : [];

    if (secciones.length === 0) {
        return null;
    }

    return {
        id: Number(payload?.id ?? 1),
        titulo: String(payload?.titulo ?? payload?.nombre ?? payloadSections?.[0]?.titulo ?? ''),
        secciones: secciones.sort((a, b) => a.orden - b.orden)
    };
};

export const getTechnicalVisitQuestions = (formDefinition: TechnicalVisitFormDefinition) =>
    formDefinition.secciones
        .flatMap((section) => section.preguntas)
        .slice()
        .sort((a, b) => a.orden - b.orden);

export const resolveTechnicalVisitId = (source: any): number | null => {
    const value =
        source?.idVisitaTecnica ??
        source?.visitaTecnica?.id ??
        source?.technicalVisit?.id ??
        source?.technicalVisitId ??
        source?.data?.idVisitaTecnica ??
        source?.data?.id;

    const normalized = Number(value);
    return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
};

export const getTechnicalVisitResponses = (source: any): any[] => {
    const payload = source?.data ?? source;
    if (Array.isArray(payload?.respuestas)) return payload.respuestas;
    if (Array.isArray(payload?.responses)) return payload.responses;
    return [];
};

export const parseTechnicalVisitResponse = (
    value: unknown,
    question: TechnicalVisitQuestion
): TechnicalVisitParsedResponse => {
    if (typeof value === 'object' && value !== null) {
        const normalizedValue = value as any;
        const answerId = Number(
            normalizedValue?.idTipoRespuesta ??
                normalizedValue?.answerId ??
                normalizedValue?.idRespuesta
        );
        const recomendacionesSeleccionadas = Array.isArray(
            normalizedValue?.recomendacionesSeleccionadas
        )
            ? normalizedValue.recomendacionesSeleccionadas
                  .map((recomendacion: any) => Number(recomendacion?.id ?? recomendacion))
                  .filter((id: number) => Number.isFinite(id) && id > 0)
            : [];

        return {
            answerId: getAnswerIdFromPayloadType(
                question,
                Number.isFinite(answerId) && answerId > 0 ? answerId : undefined
            ),
            answerDescription:
                normalizedValue?.descripcion ??
                normalizedValue?.respuesta ??
                normalizedValue?.valor,
            recomendacionesSeleccionadas
        };
    }

    if (typeof value !== 'string') {
        return {};
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) return {};

    const matchingAnswer = question.tiposRespuesta.find((answer) => {
        const normalizedDescription = answer.descripcion.trim().toUpperCase();
        const normalizedValueText = trimmedValue.toUpperCase();

        return (
            normalizedValueText === normalizedDescription ||
            normalizedValueText.startsWith(`${normalizedDescription}:`) ||
            normalizedValueText.startsWith(`${normalizedDescription} -`) ||
            normalizedValueText.startsWith(`${normalizedDescription}|`)
        );
    });

    if (!matchingAnswer) {
        return {};
    }

    return {
        answerId: matchingAnswer.id,
        answerDescription: matchingAnswer.descripcion
    };
};

export const buildTechnicalVisitPayloadResponses = ({
    formDefinition,
    selectedAnswers,
    selectedRecomendaciones
}: {
    formDefinition: TechnicalVisitFormDefinition;
    selectedAnswers: Record<number, number>;
    selectedRecomendaciones: Record<number, number[]>;
}): TechnicalVisitPayloadResponse[] =>
    getTechnicalVisitQuestions(formDefinition).map((question) => {
        const selectedAnswer = question.tiposRespuesta.find(
            (answer) => answer.id === selectedAnswers[question.id]
        );

        if (!selectedAnswer) {
            return {
                idSeccion: question.id,
                idTipoRespuesta: getPayloadAnswerTypeId(question, getDefaultAnswerId(question))
            };
        }

        return {
            idSeccion: question.id,
            idTipoRespuesta: getPayloadAnswerTypeId(question, selectedAnswer.id),
            ...(selectedAnswer.requiereRecomendacion
                ? { idsRecomendaciones: selectedRecomendaciones[question.id] ?? [] }
                : {})
        };
    });
